import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { Form as FormikForm, Formik } from "formik";
import {
  BarElement,
  CategoryScale,
  ActiveElement,
  Chart as ChartJS,
  ChartEvent,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import "./Statistics.css";
import { getAllShootingEventsConfigs } from "../../configs/utils/ShootingEventConfigFactory";
import {
  getRememberedShootingEventId,
  rememberShootingEventId
} from "../../configs/utils/ShootingEventSelectionStorage";
import { useAuth } from "../../contexts/AuthContext";
import { ShootingEventConfig } from "../../types/ShootingEventConfig";
import { ShootingSession } from "../../types/ShootingSession";
import { backendURL, useApiFetch } from "../../utils/api";
import { getScoreOrderForEvent } from "../../components/EventRankingTable";
import {
  calculateStatisticsSummary,
  filterStatisticsSessions,
  GroupingMetricKey,
  StatisticsFilters
} from "./statisticsUtils";
import {
  clearRememberedStatisticsShotCount,
  getRememberedStatisticsShotCount,
  rememberStatisticsShotCount
} from "./statisticsShotCountStorage";
import FormInput from "../../utils/form/FormInput";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const createInitialFilters = (shootingEventId: string): StatisticsFilters => ({
  shootingEventId,
  dateFrom: "",
  dateTo: "",
  shotCount: getRememberedStatisticsShotCount(shootingEventId)
});

const createClearedFilters = (shootingEventId: string): StatisticsFilters => ({
  shootingEventId,
  dateFrom: "",
  dateTo: "",
  shotCount: ""
});

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString("pl-PL");
}

function formatNumber(value: number, fractionDigits = 1): string {
  return value.toLocaleString("pl-PL", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
}

function formatMetric(value: number, suffix = "", fractionDigits = 1): string {
  return `${formatNumber(value, fractionDigits)}${suffix}`;
}

const groupingMetricLabels: Record<GroupingMetricKey, string> = {
  meanRadius: "Śr. promień",
  maxSpread: "Maks. rozrzut",
  groupCenterOffset: "Odchylenie od środka",
  groupWidth: "Szerokość",
  groupHeight: "Wysokość"
};

function Statistics() {
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { getAccountId } = useAuth();
  const accountId = getAccountId();
  const shootingEventConfigs = useMemo<ShootingEventConfig[]>(() => getAllShootingEventsConfigs(), []);
  const defaultShootingEventId = useMemo(
    () => getRememberedShootingEventId(shootingEventConfigs),
    [shootingEventConfigs]
  );

  const [sessions, setSessions] = useState<ShootingSession[]>([]);
  const [filters, setFilters] = useState<StatisticsFilters>(() => createInitialFilters(defaultShootingEventId));
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const filteredSessions = useMemo(
    () => filterStatisticsSessions(sessions, filters),
    [sessions, filters]
  );
  const scoreOrder = useMemo(
    () => getScoreOrderForEvent(filters.shootingEventId),
    [filters.shootingEventId]
  );
  const statistics = useMemo(
    () => calculateStatisticsSummary(filteredSessions, scoreOrder),
    [filteredSessions, scoreOrder]
  );
  const selectedEventName = useMemo(() => {
    return shootingEventConfigs.find((config) => config.shootingEventId === filters.shootingEventId)?.shootingEventName ?? "";
  }, [filters.shootingEventId, shootingEventConfigs]);
  const filtersApplied = Boolean(
    filters.dateFrom ||
    filters.dateTo ||
    filters.shotCount ||
    filters.shootingEventId !== defaultShootingEventId
  );

  useEffect(() => {
    if (!accountId) {
      setHasLoaded(true);
      return;
    }

    const fetchSessions = async () => {
      try {
        const data = await apiFetch<ShootingSession[]>(`${backendURL}/shooting-sessions/reporter/${accountId}`, {
          method: "GET",
          includeAuth: true
        });

        setSessions(data ?? []);
      } catch (err) {
        console.error("Error fetching user sessions for statistics:", err);
      } finally {
        setHasLoaded(true);
      }
    };

    fetchSessions();
  }, []);

  const handleFilterChange = <K extends keyof StatisticsFilters>(name: K, value: StatisticsFilters[K]) => {
    if (name === "shootingEventId") {
      rememberShootingEventId(value);
      setFilters((prev) => ({
        ...prev,
        shootingEventId: value,
        shotCount: getRememberedStatisticsShotCount(value)
      }));
      return;
    }

    if (name === "shotCount") {
      rememberStatisticsShotCount(filters.shootingEventId, value);
    }

    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    clearRememberedStatisticsShotCount(filters.shootingEventId);
    clearRememberedStatisticsShotCount(defaultShootingEventId);
    setFilters(createClearedFilters(defaultShootingEventId));
  };

  const trendLabels = statistics.sessionSummaries.map((summary) => formatDate(summary.dateValue));
  const chartTextColor = "#495057";
  const navigateToSessionSummary = (summaryIndex: number) => {
    const sessionSummary = statistics.sessionSummaries[summaryIndex];
    if (sessionSummary) {
      navigate(`/session-reports/${sessionSummary.session.id}`);
    }
  };
  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          color: chartTextColor,
          maxRotation: 0,
          autoSkip: true
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: chartTextColor
        }
      }
    }
  };
  const sessionChartOptions = {
    ...commonChartOptions,
    onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
      if (elements.length > 0) {
        navigateToSessionSummary(elements[0].index);
      }
    },
    onHover: (event: ChartEvent, elements: ActiveElement[]) => {
      const target = event.native?.target;
      if (target instanceof HTMLElement) {
        target.style.cursor = elements.length > 0 ? "pointer" : "default";
      }
    }
  };
  const percentSessionChartOptions = {
    ...sessionChartOptions,
    plugins: {
      ...sessionChartOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context: { parsed: { y: number } }) => `${formatNumber(context.parsed.y, 1)}%`
        }
      }
    },
    scales: {
      ...sessionChartOptions.scales,
      y: {
        ...sessionChartOptions.scales.y,
        ticks: {
          ...sessionChartOptions.scales.y.ticks,
          callback: (value: string | number) => `${value}%`
        }
      }
    }
  };

  const scoreTrendData = {
    labels: trendLabels,
    datasets: [
      {
        data: statistics.sessionSummaries.map((summary) => summary.totalScore),
        borderColor: "#1370d3",
        backgroundColor: "rgba(19, 112, 211, 0.16)",
        pointBackgroundColor: "#1370d3",
        pointRadius: 4,
        tension: 0.25
      }
    ]
  };

  const tenEfficiencyData = {
    labels: trendLabels,
    datasets: [
      {
        data: statistics.sessionSummaries.map((summary) => Number(summary.tenEfficiency.toFixed(2))),
        backgroundColor: "#10ac84",
        borderRadius: 4
      }
    ]
  };

  const distributionData = {
    labels: statistics.scoreDistribution.map((entry) => entry.score),
    datasets: [
      {
        data: statistics.scoreDistribution.map((entry) => entry.count),
        backgroundColor: "#e5a100",
        borderRadius: 4
      }
    ]
  };

  const renderEmptyState = () => {
    if (!hasLoaded) {
      return null;
    }

    if (sessions.length === 0) {
      return (
        <div className="statistics-empty">
          <p className="mb-3">Nie masz jeszcze zapisanych sesji.</p>
          <Button variant="dark" onClick={() => navigate("/new-session")}>Dodaj pierwszą sesję</Button>
        </div>
      );
    }

    if (filteredSessions.length === 0) {
      return (
        <div className="statistics-empty">
          <p className="mb-3">Brak danych dla wybranej konkurencji, zakresu dat i liczby strzałów.</p>
          <Button variant="secondary" onClick={resetFilters}>Wyczyść filtry</Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Container>
      <Row>
        <Col xl={11} lg={12} xs={12} className="mx-auto">
          <div className="py-3 statistics-page">
            <h1 className="font-weight-light text-center mb-4 statistics-title">Moje statystyki</h1>

            {!accountId && (
              <Alert variant="warning">Nie udało się odczytać identyfikatora konta. Zaloguj się ponownie.</Alert>
            )}

            <Formik<StatisticsFilters>
              initialValues={filters}
              enableReinitialize
              onSubmit={() => undefined}
            >
              <FormikForm className="statistics-filters mb-3">
                <div className="statistics-event-filter">
                  <Form.Label htmlFor="statisticsShootingEvent">Konkurencja</Form.Label>
                  <Form.Select
                    id="statisticsShootingEvent"
                    value={filters.shootingEventId}
                    onChange={(event) => handleFilterChange("shootingEventId", event.target.value)}
                  >
                    {shootingEventConfigs.map((config) => (
                      <option key={config.shootingEventId} value={config.shootingEventId}>
                        {config.shootingEventName}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <div className="statistics-date-filter">
                  <Form.Label htmlFor="statisticsDateFrom">Od</Form.Label>
                  <FormInput
                    name="dateFrom"
                    placeholder="dd.mm.rrrr"
                    type="date"
                    className=""
                    inputId="statisticsDateFrom"
                    required={false}
                    onValueChange={(value) => handleFilterChange("dateFrom", value)}
                  />
                </div>

                <div className="statistics-date-filter">
                  <Form.Label htmlFor="statisticsDateTo">Do</Form.Label>
                  <FormInput
                    name="dateTo"
                    placeholder="dd.mm.rrrr"
                    type="date"
                    className=""
                    inputId="statisticsDateTo"
                    required={false}
                    onValueChange={(value) => handleFilterChange("dateTo", value)}
                  />
                </div>

                <div className="statistics-shot-filter">
                  <Form.Label htmlFor="statisticsShotCount">Liczba strzałów</Form.Label>
                  <Form.Control
                    id="statisticsShotCount"
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="np. 10"
                    value={filters.shotCount}
                    onChange={(event) => handleFilterChange("shotCount", event.target.value)}
                  />
                </div>

                <Button
                  variant="secondary"
                  className="statistics-filter-action"
                  disabled={!filtersApplied}
                  type="button"
                  onClick={resetFilters}
                >
                  Wyczyść filtry
                </Button>
              </FormikForm>
            </Formik>

            {renderEmptyState()}

            {filteredSessions.length > 0 && (
              <div className="statistics-dashboard">
                <div className="statistics-context">
                  <span>{selectedEventName}</span>
                  <span>{statistics.sessionCount} sesji w zestawieniu</span>
                </div>

                <div className="statistics-tiles">
                  <MetricTile label="Sesje" value={statistics.sessionCount.toString()} />
                  <MetricTile label="Strzały" value={statistics.totalDeclaredShots.toString()} />
                  <MetricTile label="Śr. wynik sesji" value={formatMetric(statistics.averageSessionScore)} />
                  <MetricTile label="Śr. punkt/strzał" value={formatMetric(statistics.averageScorePerShot, "", 2)} />
                  <MetricTile
                    label="Najlepsza sesja"
                    value={formatMetric(statistics.bestSession?.totalScore ?? 0, "", 0)}
                    onClick={statistics.bestSession ? () => navigate(`/session-reports/${statistics.bestSession?.session.id}`) : undefined}
                  />
                  <MetricTile
                    label="Najlepsza tarcza"
                    value={formatMetric(statistics.bestTargetScore, "", 0)}
                    onClick={statistics.bestTarget ? () => navigate(`/session-reports/${statistics.bestTarget?.session.id}`) : undefined}
                  />
                </div>

                <div className="statistics-charts">
                  <ChartCard title="Postęp wyniku" subtitle="Suma punktów w kolejnych sesjach">
                    <Line data={scoreTrendData} options={sessionChartOptions} />
                  </ChartCard>

                  <ChartCard title="Udział 10-ek" subtitle="Procent strzałów trafionych w pole 10">
                    <Bar data={tenEfficiencyData} options={percentSessionChartOptions} />
                  </ChartCard>

                  <ChartCard title="Rozkład punktów" subtitle="Wszystkie ocenione trafienia w wybranym zakresie">
                    <Bar data={distributionData} options={commonChartOptions} />
                  </ChartCard>

                  <div className="statistics-panel">
                    <h2>Skupienie</h2>
                    {statistics.groupingExtremes.length > 0 ? (
                      <div className="statistics-grouping-extremes">
                        <GroupingExtremeColumn title="Min" variant="min">
                          {statistics.groupingExtremes.map((extreme) => (
                            <GroupingExtremeButton
                              key={`min-${extreme.metric}`}
                              label={groupingMetricLabels[extreme.metric]}
                              value={formatMetric(extreme.min.value, " mm", 2)}
                              onClick={() => navigate(`/session-reports/${extreme.min.session.id}`)}
                            />
                          ))}
                        </GroupingExtremeColumn>

                        <GroupingExtremeColumn title="Max" variant="max">
                          {statistics.groupingExtremes.map((extreme) => (
                            <GroupingExtremeButton
                              key={`max-${extreme.metric}`}
                              label={groupingMetricLabels[extreme.metric]}
                              value={formatMetric(extreme.max.value, " mm", 2)}
                              onClick={() => navigate(`/session-reports/${extreme.max.session.id}`)}
                            />
                          ))}
                        </GroupingExtremeColumn>
                      </div>
                    ) : (
                      <p className="statistics-muted mb-0">Brak zapisanych współrzędnych trafień dla tego zakresu.</p>
                    )}
                  </div>
                </div>

                <div className="statistics-highlight-list">
                  {statistics.bestSession && (
                    <button
                      type="button"
                      className="statistics-highlight-tile"
                      onClick={() => navigate(`/session-reports/${statistics.bestSession?.session.id}`)}
                    >
                      <span>Najlepsza sesja</span>
                      <strong>{formatMetric(statistics.bestSession.totalScore, "", 0)} pkt</strong>
                      <span>{formatDate(statistics.bestSession.dateValue)}</span>
                    </button>
                  )}

                  {statistics.bestTarget && (
                    <button
                      type="button"
                      className="statistics-highlight-tile"
                      onClick={() => navigate(`/session-reports/${statistics.bestTarget?.session.id}`)}
                    >
                      <span>Najlepsza tarcza</span>
                      <strong>{formatMetric(statistics.bestTarget.score, "", 0)} pkt</strong>
                      <span>{formatDate(statistics.bestTarget.dateValue)}</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

interface MetricTileProps {
  label: string;
  value: string;
  compact?: boolean;
  onClick?: () => void;
}

function MetricTile({ label, value, compact = false, onClick }: MetricTileProps) {
  const className = compact ? "statistics-tile statistics-tile-compact" : "statistics-tile";
  const content = (
    <>
      <span>{label}</span>
      <strong>{value}</strong>
    </>
  );

  if (onClick) {
    return (
      <button type="button" className={`${className} statistics-tile-button`} onClick={onClick}>
        {content}
      </button>
    );
  }

  return (
    <div className={className}>
      {content}
    </div>
  );
}

interface GroupingExtremeColumnProps {
  title: string;
  variant: "min" | "max";
  children: React.ReactNode;
}

function GroupingExtremeColumn({ title, variant, children }: GroupingExtremeColumnProps) {
  return (
    <div className={`statistics-grouping-column statistics-grouping-column-${variant}`}>
      <h3>{title}</h3>
      <div className="statistics-grouping-column-items">
        {children}
      </div>
    </div>
  );
}

interface GroupingExtremeButtonProps {
  label: string;
  value: string;
  onClick: () => void;
}

function GroupingExtremeButton({ label, value, onClick }: GroupingExtremeButtonProps) {
  return (
    <button type="button" className="statistics-grouping-extreme-button" onClick={onClick}>
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

interface ChartCardProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

function ChartCard({ title, subtitle, children }: ChartCardProps) {
  return (
    <div className="statistics-panel">
      <div className="statistics-panel-heading">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <div className="statistics-chart-container">
        {children}
      </div>
    </div>
  );
}

export default Statistics;
